import { useEffect, useCallback, useState } from 'react';

import { useScriptLoad } from './hooks';
import { useCustomerPrivacy } from './hooks/useCustomerPrivacy';
import { changeFueledConsent } from './fueled.utils';
import { useWaitForLoadScript } from './useWaitForLoadScript';

const CONSENT_GROUPS = {
  STRICTLY_NECESSARY: 'C0001',
  PERFORMANCE: 'C0002',
  FUNCTIONAL: 'C0003',
  TARGETING: 'C0004',
};

export const useGetOneTrustConsent = () => {
  const [consent, setConsent] = useState(null);

  const { customerPrivacy } = useCustomerPrivacy({
    checkoutDomain: process.env.SHOPIFY_CART_DOMAIN || 'shop.briogeohair.com',
    storefrontAccessToken:
      process.env.SHOPIFY_STOREFRONT_API_TOKEN,
    onVisitorConsentCollected: (newConsent) => {
      console.log('Visitor consent collected:', newConsent);

      setConsent({
        marketing: newConsent.marketingAllowed,
        analytics: newConsent.analyticsAllowed,
        preferences: newConsent.preferencesAllowed,
        sale_of_data: newConsent.saleOfDataAllowed,
      });
    },
  });

  const isOneTrustLoaded = useWaitForLoadScript(() => {
    return window.OneTrust;
  });

  const oneTrustConsentCallback = useCallback(() => {
    if (!customerPrivacy?.setTrackingConsent) {
      return;
    }

    const trackingConsent = {
      marketing: getOneTrustConsentStatus(CONSENT_GROUPS.TARGETING),
      analytics: getOneTrustConsentStatus(CONSENT_GROUPS.PERFORMANCE),
      preferences:
        getOneTrustConsentStatus(CONSENT_GROUPS.STRICTLY_NECESSARY) ||
        getOneTrustConsentStatus(CONSENT_GROUPS.FUNCTIONAL),
      sale_of_data:
        getOneTrustConsentStatus(CONSENT_GROUPS.PERFORMANCE) &&
        getOneTrustConsentStatus(CONSENT_GROUPS.TARGETING),
    };

    customerPrivacy.setTrackingConsent(trackingConsent, () => {});
  }, [customerPrivacy]);

  useEffect(() => {
    window.OptanonWrapper = oneTrustConsentCallback;
    
    if (isOneTrustLoaded) {
      oneTrustConsentCallback();
      window.OneTrust.OnConsentChanged(oneTrustConsentCallback);

      return () => {
        window.removeEventListener('OnConsentChanged', oneTrustConsentCallback);
      };
    }
  }, [isOneTrustLoaded, oneTrustConsentCallback]);

  useEffect(() => {
    if (consent) {
      changeFueledConsent(consent);
    }

    const handleFueledReady = () => {
      if (!consent) return;

      changeFueledConsent(consent);
    };

    window.addEventListener('fueled-ready', handleFueledReady);

    return () => {
      window.removeEventListener('fueled-ready', handleFueledReady);
    };
  }, [consent]);

  return consent;
};

export const FueledOneTrustScript = ({ withKey: openTrustId }) => {
  useScriptLoad({
    id: 'fueled-open-trust',
    charset: 'UTF-8',
    src: `https://cdn.cookielaw.org/scripttemplates/otSDKStub.js`,
    'data-domain-script': openTrustId,
  }, 'head');

  return null;
};

export function getOneTrustConsentStatus(groupId) {
  try {
    return window.OnetrustActiveGroups?.includes(groupId) || false;
  } catch (e) {
    console.error('Error checking OneTrust consent status:', e);
    return false;
  }
}